import dataUtils from '@/common/dataUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { cacheAllModuleFiles, updateModuleCache } from '@/store/sync/cachingUtils';
import { SYNC_STATES } from '@/store/sync/sync';
import api from './api';
import moduleIndex from './index.js';
import MODULE_ID from './moduleId';
import constants from './constants';

import Vue from 'vue';

const state = {
  // Data loaded from database
  modules: [],
  questions: [],
  answers: [],
  reportAnswers: [],
  medias: [],
  userStates: [],

  // Lookup maps
  answersByQuestionId: {}, //id:Array of answers
  modulesById: {},
  userStateByModuleId: {},
  userAnswersByQuestionId: {}, //objects of answerId and value

  // view states
  dataLoaded: false,
  currentQuestion: null,
  unreadCount: 0,
  unreadCountsByInstance: {},

  dialog: {
    id: null, // dialog id that is provided with callbacks
    isOpen: false, // true=open
    title: null, // dialog title
    text: null, // dialog content
    okText: null, //text for ok button
    cancelText: null, // text for cancel button
    params: null, //optional parameter object
    next: null, // router next that can be set
  },
};

const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    return state.unreadCountsByInstance[instance];
  },

  getModuleStatistics: (state) => (module) => {
    let moduleQuestions = state.questions.filter((q) => q.module_id === module.id);
    let correctOnes = 0;
    let totalCorrectAnswers = 0;
    let totalFalseAnswers = 0;

    let answeredQuestions = moduleQuestions.filter((q, ind) => {
      let userAnswersById = state.userAnswersByQuestionId[q.id];
      let expectedAnswers = state.answersByQuestionId[q.id] || [];
      if (!userAnswersById) return false;

      let correctAnswersCount = expectedAnswers.filter((expectedAnswer) => isCorrectAnswer(userAnswersById, expectedAnswer)).length;
      totalCorrectAnswers += correctAnswersCount;
      let falseAnswersCount = expectedAnswers.length - correctAnswersCount;
      totalFalseAnswers += falseAnswersCount;
      // If all answers are correct, increase count
      if (correctAnswersCount === expectedAnswers.length) correctOnes++;

      let userAnswersCount;
      if (userAnswersById._freezed) {
        userAnswersCount = Object.keys(userAnswersById).length - 1;
      } else {
        return false;
      }
      return userAnswersCount > 0;
    });

    const response = {
      questionsCount: moduleQuestions.length,
      answeredQuestionsCount: answeredQuestions.length,
      correctOnes,
      totalFalseAnswers,
      totalCorrectAnswers,
    };
    return response;
  },
  isCorrectAnswer: (state) => (userAnswersById, expectedAnswer) => {
    return isCorrectAnswer(userAnswersById, expectedAnswer);
  },
};

function isCorrectAnswer(userAnswersById, expectedAnswer) {
  if (!userAnswersById) return false;
  if (!userAnswersById[expectedAnswer.id] && expectedAnswer.is_correct === 0) return true;
  if (userAnswersById[expectedAnswer.id] === true && expectedAnswer.is_correct === 1) return true;
  if (userAnswersById[expectedAnswer.id] === false && expectedAnswer.is_correct === 0) return true; // ist das nicht gleich wie das zweite if?
  return false;
}

const mutations = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    console.debug('elearning countModuleItems');

    let unreadCountsByInstance = {};

    state.modules
      .filter((m) => {
        let userState = state.userStateByModuleId[m.id];
        return !userState || userState.status === constants.STATUS.starting;
      })
      .forEach((m) => {
        unreadCountsByInstance[m.instance] = Number(unreadCountsByInstance[m.instance] || 0) + 1;
      });

    state.unreadCountsByInstance = unreadCountsByInstance;
  },

  /**
   * Sets one or more of the possible dialog values which is rendered in elearningHeader
   *
   * @param {*} state
   * @param {object} dialogProperties (see CommonDialog)
   *     id - dialog id that is provided with callbacks
   *     isOpen - true to make visible
   *     title - dialog title
   *     text - dialog content
   *     okText - text for ok button
   *     cancelText - text for cancel button
   *     next - router next that can be set
   */
  setDialog(state, dialogProperties) {
    state.dialog = dialogProperties;
    console.debug('setDialog with', JSON.stringify(dialogProperties, null, 2));
  },
  setCurrentQuestion(state, currentQuestion) {
    state.currentQuestion = currentQuestion;
  },

  setUserAnswer(state, { questionId, answersByAnswerId }) {
    const currentAnswers = state.userAnswersByQuestionId[questionId];
    if (currentAnswers && currentAnswers._freezed) {
      answersByAnswerId = currentAnswers;
    }
    Vue.set(state.userAnswersByQuestionId, questionId, answersByAnswerId);
    localStorage.setItem('elearning.userAnswersByQuestionId', JSON.stringify(state.userAnswersByQuestionId));
  },

  freezeUserAnswer(state, { questionId }) {
    let userAnswers = state.userAnswersByQuestionId[questionId] || {};
    userAnswers._freezed = true;
    console.debug('freezeUserAnswer', questionId, userAnswers);
    Vue.set(state.userAnswersByQuestionId, questionId, userAnswers);
  },

  resetUserAnswer(state, { moduleId }) {
    console.debug('resetUserAnswer', moduleId);
    state.questions
      .filter((q) => {
        return q.module_id == moduleId;
      })
      .forEach((q) => {
        Vue.delete(state.userAnswersByQuestionId, q.id);
      });
    localStorage.setItem('elearning.userAnswersByQuestionId', JSON.stringify(state.userAnswersByQuestionId));
  },

  setUserState(state, { module, status }) {
    let userState = state.userStateByModuleId[module.id];
    const date = new Date().toISOString();
    userState.dateupdated = date;

    userState.datecreated = userState.datecreated ? userState.datecreated : date;
    userState.status = status;
    console.debug('elearning setUserState', state.userStateByModuleId[module.id]);
  },

  setData(state, data) {
    Object.keys(data).forEach((k) => {
      if (['userStateByModuleId', 'userAnswersByQuestionId'].includes(k)) {
        //userAnswersByQuestionId
        console.debug('set reactive data ', k);
        state[k] = data[k]; // reactive properties
      } else {
        state[k] = Object.freeze(data[k]);
      }
    });
    state.dataLoaded = true;
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.modules,
      titleSearchAttr: 'name',
      contentSearchAttr: 'description',
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    // const { modules, questions, answers, medias, userStates } = state;
    // await updateModuleCache(moduleIndex.api, { modules, questions, answers, medias, userStates });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    await cacheAllModuleFiles({ files: state.medias, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }) {
    try {
      const result = await api.load();
      let { modules, questions, answers, medias, reportAnswers, userStates, userId } = result.data;

      // TODO: Create id maps if necessary
      const userStateByModuleId = dataUtils.buildIdMap(userStates, 'moduleIdFk');
      const modulesById = dataUtils.buildIdMap(modules, 'id');

      modules.forEach((module) => {
        let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({
          moduleType: MODULE_ID,
          instance: module.instance,
        });

        module._displayLocation = '';
        module._moduleTitle = moduleConfig ? moduleConfig.title : 'E-Learning';
        if (module.is_practice) {
          module._path = `/${moduleIndex.path}/training/${module.instance}/${module.id}`;
        } else {
          module._path = `/${moduleIndex.path}/exam/${module.instance}/${module.id}`; //TODO: Create route for opening fahrdienst document directly
        }

        let userState = userStateByModuleId[module.id];
        if (!userState) {
          userState = {
            moduleIdFk: module.id,
            userIdFk: userId,
            userdatemodified: '',
            datecreated: null,
            dateupdated: null,
          };
          userStateByModuleId[module.id] = userState;
        }
        // assert status is set
        if (!userState.status) {
          userState.status = constants.STATUS.starting;
        }

        // assert _type is set
        userState._type = 'elearningUserState';
      });

      let answersByQuestionId = dataUtils.buildArrayMapById(answers, (answer) => answer.question_id);

      let userAnswersByQuestionId = JSON.parse(localStorage.getItem(constants.LOCAL_STORAGE_KEY) || '{}');
      commit('setData', {
        modules,
        modulesById,
        questions,
        answers,
        medias,
        userStates,
        answersByQuestionId,
        userStateByModuleId,
        userAnswersByQuestionId,
        reportAnswers,
        userId,
        // questionsByModuleId,
        // answersByQuestionId,
      });
    } catch (err) {
      console.error('Failed to load elearning metadata', err);
      commit(
        'sync/setSyncState',
        {
          moduleId: MODULE_ID,
          syncState: SYNC_STATES.FAILURE,
          msg: `${err.code}:${err.message}`,
        },
        { root: true }
      );
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
